/* ============================================================================
   M6 — Courier COD Reconciliation   Route: /supply/cod-recon
   Supply-side / courier-side COD only (Courier → Prozo). Five sub-tabs:
   Dashboard · Reconciliation Workspace · Remittance Upload · Disputes · Cycle
   Config. Source of truth for Expected Remittance Date is the Cycle Config tab.
   React port of the mockup js/m6.js — built on the shared Supply-Side components
   so the scoped mockup CSS applies 1:1.
   ========================================================================== */
import React, {useMemo, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import BoltIcon from '@mui/icons-material/Bolt'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PageTitle from '../../../components/PageTitle'
import DashboardTab from './DashboardTab'
import WorkspaceTab from './WorkspaceTab'
import UploadTab from './UploadTab'
import DisputesTab from './DisputesTab'
import ConfigTab from './ConfigTab'
import {visibleVerticals} from './codLogic'

const TABS = [
  {id: 'dashboard', label: 'Dashboard'},
  {id: 'workspace', label: 'Reconciliation Workspace'},
  {id: 'upload', label: 'Remittance Upload'},
  {id: 'disputes', label: 'Disputes'},
  {id: 'config', label: 'Cycle Config'},
]

export default function CodRecon() {
  const [params, setParams] = useSearchParams()
  const verticals = visibleVerticals()

  const urlTab = params.get('tab')
  const initial = TABS.some((t) => t.id === urlTab) ? urlTab : 'dashboard'
  const [tab, setTab] = useState(initial)
  /* workspace deep-link seed from a dashboard KPI click */
  const [seed, setSeed] = useState(null)

  const go = (id) => {
    setTab(id)
    const next = new URLSearchParams(params)
    next.set('tab', id)
    setParams(next, {replace: true})
  }

  const seedWorkspace = (statuses, courier, gotoTab) => {
    if (gotoTab === 'disputes') {
      go('disputes')
      return
    }
    setSeed({statuses, courier, nonce: Date.now()})
    go('workspace')
  }

  const content = useMemo(() => {
    switch (tab) {
      case 'workspace':
        return (
          <WorkspaceTab
            key={seed ? seed.nonce : 'ws'}
            statusSeed={seed ? seed.statuses : null}
            courierSeed={seed ? seed.courier : null}
          />
        )
      case 'upload':
        return <UploadTab onGotoDisputes={() => go('disputes')} />
      case 'disputes':
        return <DisputesTab />
      case 'config':
        return <ConfigTab />
      case 'dashboard':
      default:
        return <DashboardTab onSeedWorkspace={seedWorkspace} />
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, seed])

  if (!verticals.length) {
    return (
      <>
        <PageTitle>COD Reconciliation</PageTitle>
        <div className='gate'>
          <div className='lock'>
            <LockOutlinedIcon style={{width: 30, height: 30}} />
          </div>
          <h2>No COD data in your scope</h2>
          <p>COD Reconciliation requires D2C Freight Commercials access.</p>
        </div>
      </>
    )
  }

  return (
    <>
      <PageTitle>Courier COD Reconciliation</PageTitle>
      <div className='page-head'>
        <div>
          <h1>Courier COD Reconciliation</h1>
          <p className='sub'>
            Supply-side COD only — Courier → Prozo. Client/merchant-side settlement is a separate
            workstream and out of scope here.
          </p>
        </div>
        <span className='banner'>
          <BoltIcon style={{width: 13, height: 13}} />
          {verticals.join(' + ')} data · ProShip Admin
        </span>
      </div>

      <div className='tabs' role='tablist'>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={'tab' + (t.id === tab ? ' active' : '')}
            role='tab'
            aria-selected={t.id === tab}
            onClick={() => {
              if (t.id !== 'workspace') setSeed(null)
              go(t.id)
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div key={tab} style={{animation: 'pageIn .25s var(--ease)'}}>
        {content}
      </div>
    </>
  )
}
